import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from "@chakra-ui/react";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { create, enforce, test } from "vest";

import { Option } from "@/jenesys-types/list";

import { Form } from "@/components/forms/Form/Form";
import { FormActions } from "@/components/forms/FormActions/FormActions";
import { FormInput } from "@/components/forms/FormBaseInput/FormBaseInput";
import { FormField } from "@/components/forms/FormField/FormField";
import { FormSelect } from "@/components/forms/FormSelect/FormSelect";
import { FormStack } from "@/components/forms/FormStack/FormStack";
import { loadBankConnections } from "@/store/features/clientSettings/connections/connections";
import {
  addBank,
  selectReconciliation
} from "@/store/features/reconciliation/reconciliation";
import { toastError } from "@/util/helpers";

interface FormData {
  name: string;
  accountName: string;
  accountNumber: string;
  accountType: string;
}

interface AddBankProps {
  isOpen: boolean;
  onClose: () => void;
}

const defaultBankDetails: FormData = {
  name: "",
  accountName: "",
  accountNumber: "",
  accountType: "savings"
};

const accountTypeOptions: Array<Option> = [
  { value: "savings", label: "Savings" },
  { value: "current", label: "Current" },
  { value: "checking", label: "Checking" }
];

// Validation schema for the AddBank form
const addBankValidationSchema = create((data: FormData) => {
  test("name", "Bank name is required", () => {
    enforce(data.name).isNotEmpty();
  });

  test(
    "name",
    "Bank name can only contain letters, numbers, and spaces",
    () => {
      enforce(data.name).matches(/^[a-zA-Z0-9\s]+$/);
    }
  );

  test("accountName", "Account name is required", () => {
    enforce(data.accountName).isNotEmpty();
  });

  test(
    "accountName",
    "Account name can only contain letters, numbers, and spaces",
    () => {
      enforce(data.accountName).matches(/^[a-zA-Z0-9\s]+$/);
    }
  );

  test("accountNumber", "Account number is required", () => {
    enforce(data.accountNumber).isNotEmpty();
  });

  test("accountNumber", "Account number must be a number", () => {
    enforce(data.accountNumber).matches(/^\d+$/);
  });
});

export const AddBank: React.FC<AddBankProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { clientId } = useParams();
  const { addBankLoading } = useSelector(selectReconciliation);

  const onSubmit = (formData: FormData) => {
    const { name, accountName, accountNumber, accountType } = formData;
    if (!name || !accountName || !accountNumber || !accountType) {
      toastError("Please fill in all fields");
      return;
    }
    dispatch(
      addBank({
        companyId: clientId!,
        bankData: {
          name,
          account_name: accountName,
          account_number: accountNumber,
          account_type: accountType,
          company_id: clientId
        },
        onSuccess: () => {
          dispatch(loadBankConnections(clientId!));
          onClose();
        }
      })
    );
  };

  return (
    <Modal isCentered size="xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <Form
        onSubmit={onSubmit}
        initialValues={defaultBankDetails}
        validationSchema={addBankValidationSchema}
      >
        <ModalContent px={2} py={4}>
          <ModalHeader fontSize={18} color="secondary.600">
            Add Bank and First Account
          </ModalHeader>
          <ModalCloseButton top={7} right={5} />
          <ModalBody fontSize={14} fontWeight={500} pb={4}>
            <FormStack
              w="full"
              spacing={3}
              mt={0}
              rounded={6}
              borderColor="gray.200"
            >
              <FormField
                name="name"
                label="Bank Name"
                control={FormInput}
                isRequired
              />
              <FormField
                name="accountName"
                label="Account Name"
                control={FormInput}
                isRequired
              />
              <FormField
                name="accountNumber"
                label="Account Number"
                control={FormInput}
                isRequired
              />
              <FormField
                name="accountType"
                label="Account Type"
                control={FormSelect}
                options={accountTypeOptions}
              />
            </FormStack>
          </ModalBody>

          <ModalFooter>
            <FormActions
              isSaving={addBankLoading}
              submitText="Save"
              cancelText="Cancel"
              onCancel={onClose}
            />
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
